import React, { useState, useEffect, useRef } from 'react';
import T from 'prop-types';

import Modal from '../../../../../components/Modal';
import Button from '../../../../../components/Buttons/Button';
import InputText from '../../../../../components/Inputs/InputText';
import { toast } from '../../../../../components/Toasts';

import api from '../../../api';
import { redirectToCustomReports } from '../../../utils/custom-exports-utils';

const decorateInitialName = (report, action) => {
  if (action === 'duplicate') {
    return `${report.name} (duplicate)`;
  }

  return report.name;
};

const UpsertExportModal = ({ open, report, action, onUpdateSuccess, onRenameSuccess, onClose }) => {
  const inputTextRef = useRef();

  const [name, setName] = useState(decorateInitialName(report, action));
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  const isUpsertDisabled = !name.trim() || Boolean(error) || pending;

  const handleModalClose = () => {
    if (pending) return;
    onClose();
  };

  const handleUpdateExport = () => {
    api.customExports
      .updateCustomExport(report.id, { custom_export: { name }, no_resync: true })
      .then(({ data }) => {
        setPending(false);

        toast.success('Export name has been updated successfully.');

        if (onUpdateSuccess) onUpdateSuccess(data);
        handleModalClose();
      })
      .catch(({ response: { data } }) => {
        setPending(false);
        setError(data.errors);
      });
  };

  const handleUpsertExport = (e) => {
    e.preventDefault();
    if (isUpsertDisabled) return;

    setPending(true);

    if (action === 'update' && report.id) {
      handleUpdateExport();
    } else if (action === 'duplicate') {
      redirectToCustomReports(`/new?duplicate_export_id=${report.id}&name=${name}`);
    } else if (onRenameSuccess) {
      onRenameSuccess({ ...report, name });
      handleModalClose();
    }
  };

  // Effects
  useEffect(() => {
    // Select input text on open
    inputTextRef.current.select();
  }, []);

  return (
    <Modal
      open={open}
      size="xs"
      closeButtonIconClass="hicon-close-2"
      modalClassName="upsert-export-modal"
      onClose={handleModalClose}
    >
      <form onSubmit={handleUpsertExport}>
        <div className="upsert-export-modal__body">
          <h4 className="upsert-export-modal__title">
            {action === 'update' ? 'Rename Export' : 'Name Export'}
          </h4>
          <small className="upsert-export-modal__description">
            Please choose a name for this export
          </small>
          <div className="upsert-export-modal__form-fields">
            <InputText
              ref={inputTextRef}
              value={name}
              placeholder="Export Name"
              onChange={setName}
            />
            {error && <div className="text-red text-12 mt-xxxs">{error}</div>}
          </div>
        </div>

        <div className="upsert-export-modal__footer">
          <Button size="lg" variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button size="lg" disabled={isUpsertDisabled} type="submit">
            {action === 'update' ? 'Rename' : 'Create New Export'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

UpsertExportModal.propTypes = {
  open: T.bool.isRequired,
  report: T.object.isRequired,
  action: T.oneOf(['edit', 'update', 'create', 'duplicate']),
  onUpdateSuccess: T.func,
  onRenameSuccess: T.func,
  onClose: T.func.isRequired,
};

export default UpsertExportModal;
